import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { AuthContext } from '../../../components/AuthContext';
import Swal from 'sweetalert2';
import Select from 'react-select';
import { 
  FaCalendarAlt, FaMapMarkerAlt, FaFileExport, FaSearch, FaDownload,
  FaArrowRight, FaArrowLeft, FaBox, FaWarehouse, FaTruck,
  FaExpand, FaCompress, FaTrashAlt
} from 'react-icons/fa';

export default function TilesDayBook() {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [dayBookData, setDayBookData] = useState([]);
  const [locations, setLocations] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1, totalPages: 0, totalEntries: 0, entriesPerPage: 50
  });
  const [filters, setFilters] = useState({
    dateFrom: '', dateTo: '', location: null, page: 1, limit: 50, sortBy: 'date', sortOrder: 'asc'
  });
  const [locationOptions, setLocationOptions] = useState([]);

  // Dark mode
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  useEffect(() => {
    const sync = () => setDarkMode(localStorage.getItem('darkMode') === 'true');
    const interval = setInterval(sync, 500);
    return () => clearInterval(interval);
  }, []);

  // Fullscreen
  const containerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  }, []);
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // React-select dark styles
  const getSelectStyles = (isDark) => ({
    control: (base, state) => ({
      ...base,
      backgroundColor: isDark ? '#374151' : 'white',
      borderColor: state.isFocused ? '#7c3aed' : isDark ? '#4b5563' : '#d1d5db',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(124,58,237,0.2)' : 'none',
      borderRadius: '8px',
      '&:hover': { borderColor: '#7c3aed' }
    }),
    menu: (base) => ({ ...base, backgroundColor: isDark ? '#374151' : 'white', border: isDark ? '1px solid #4b5563' : '1px solid #d1d5db', zIndex: 50 }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#7c3aed' : state.isFocused ? (isDark ? '#4b5563' : '#f3f4f6') : 'transparent',
      color: state.isSelected ? 'white' : isDark ? '#e5e7eb' : '#374151'
    }),
    singleValue: (base) => ({ ...base, color: isDark ? '#e5e7eb' : '#374151' }),
    input: (base) => ({ ...base, color: isDark ? '#e5e7eb' : '#374151' }),
    placeholder: (base) => ({ ...base, color: '#9ca3af' })
  });

  const getCompanyId = () => user?.user?.company || '';

  useEffect(() => {
    fetchLocations();
    const now = new Date();
    const threeDaysAgo = new Date();
    threeDaysAgo.setDate(now.getDate() - 3);
    setFilters({ dateFrom: threeDaysAgo.toISOString().split('T')[0], dateTo: now.toISOString().split('T')[0], location: null, page: 1, limit: 50, sortBy: 'date', sortOrder: 'asc' });
  }, []);

  useEffect(() => {
    if (filters.dateFrom && filters.dateTo) {
      fetchDayBookData(filters.page);
    }
  }, [filters.dateFrom, filters.dateTo, filters.location, filters.limit, filters.sortBy, filters.sortOrder]);

  const fetchLocations = async () => {
    try {
      const companyId = getCompanyId();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/locations${companyId ? `/companyId/${companyId}` : ''}`, { credentials: 'include' });
      const data = await response.json();
      const locs = Array.isArray(data) ? data : [];
      setLocations(locs);
      setLocationOptions(locs.map(l => ({ value: l._id, label: l.name || l.locationName, location: l })));
    } catch (error) {
      console.error('Error fetching locations:', error);
      Swal.fire('Error', 'Failed to fetch locations', 'error');
    }
  };

  const fetchDayBookData = async (pageNum = 1) => {
    if (!filters.dateFrom || !filters.dateTo) { Swal.fire('Error', 'Please select both date from and date to', 'error'); return; }
    setLoading(true);
    try {
      const companyId = getCompanyId();
      const params = new URLSearchParams({
        dateFrom: filters.dateFrom, dateTo: filters.dateTo,
        ...(filters.location && { location: filters.location.value }),
        ...(companyId && { companyId }),
        page: pageNum, limit: filters.limit, sortBy: filters.sortBy, sortOrder: filters.sortOrder, format: 'json'
      });
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/reports/tiles/daybook?${params}`, { credentials: 'include' });
      const data = await response.json();
      if (data.success) { setDayBookData(data.data || []); setPagination(data.pagination || {}); }
      else { throw new Error(data.message || 'Failed to fetch data'); }
    } catch (error) {
      console.error('Error fetching daybook data:', error);
      Swal.fire('Error', 'Failed to fetch daybook data', 'error');
      setDayBookData([]);
    } finally { setLoading(false); }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value, ...(field !== 'page' && { page: 1 }) }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
    fetchDayBookData(newPage);
  };

  const exportToExcel = async () => {
    if (!filters.dateFrom || !filters.dateTo) { Swal.fire('Error', 'Please select both date from and date to', 'error'); return; }
    setLoading(true);
    try {
      const companyId = getCompanyId();
      const params = new URLSearchParams({
        dateFrom: filters.dateFrom, dateTo: filters.dateTo,
        ...(filters.location && { location: filters.location.value }),
        ...(companyId && { companyId }),
        format: 'excel', sortBy: filters.sortBy, sortOrder: filters.sortOrder
      });
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/reports/tiles/daybook?${params}`, { credentials: 'include' });
      if (response.ok) {
        const blob = await response.blob(); const url = window.URL.createObjectURL(blob); const link = document.createElement('a');
        link.href = url; link.setAttribute('download', `Tiles_DayBook_${filters.dateFrom}_to_${filters.dateTo}.xlsx`);
        document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
        Swal.fire('Success', 'Excel file downloaded successfully', 'success');
      } else { throw new Error('Failed to download Excel file'); }
    } catch (error) { console.error('Error downloading Excel:', error); Swal.fire('Error', 'Failed to download Excel file', 'error'); }
    finally { setLoading(false); }
  };

  const exportToCSV = () => {
    if (dayBookData.length === 0) { Swal.fire('Warning', 'No data to export', 'warning'); return; }
    const headers = ['Date','Tile Name','Tile Code','Location','Supplier','ID','Inward FS Box','Inward FS Pieces','Inward DS Box','Inward DS Pieces','Outward FS Box','Outward FS Pieces','Outward DS Box','Outward DS Pieces'];
    let csvContent = headers.join(',') + '\n';
    dayBookData.forEach(entry => {
      const row = [
        entry.date ? new Date(entry.date).toLocaleDateString() : '',
        `"${entry.tileName || ''}"`, `"${entry.tileCode || ''}"`,
        `"${entry.location?.name || entry.location?.locationName || ''}"`,
        `"${entry.inward?.supplier || ''}"`, `"${entry.inward?.id || entry.outward?.id || ''}"`,
        entry.inward?.fsBox || 0, entry.inward?.fsPieces || 0, entry.inward?.dsBox || 0, entry.inward?.dsPieces || 0,
        entry.outward?.fsBox || 0, entry.outward?.fsPieces || 0, entry.outward?.dsBox || 0, entry.outward?.dsPieces || 0
      ];
      csvContent += row.join(',') + '\n';
    });
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a'); const url = URL.createObjectURL(blob);
    link.setAttribute('href', url); link.setAttribute('download', `tiles_daybook_${filters.dateFrom}_to_${filters.dateTo}.csv`);
    link.style.visibility = 'hidden'; document.body.appendChild(link); link.click(); document.body.removeChild(link);
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });

  const groupEntriesByDate = (entries) => {
    const grouped = {};
    entries.forEach(entry => {
      const dateKey = new Date(entry.date).toISOString().split('T')[0];
      if (!grouped[dateKey]) grouped[dateKey] = [];
      grouped[dateKey].push(entry);
    });
    return grouped;
  };

  const groupedData = groupEntriesByDate(dayBookData);

  // Reusable class helpers
  const cardCls = darkMode ? 'bg-gray-800/80 backdrop-blur-sm border-gray-700' : 'bg-white border-gray-200';
  const textP = darkMode ? 'text-white' : 'text-gray-900';
  const textS = darkMode ? 'text-gray-400' : 'text-gray-600';
  const textM = darkMode ? 'text-gray-500' : 'text-gray-400';
  const borderCls = darkMode ? 'border-gray-700' : 'border-gray-200';
  const inputCls = darkMode ? 'bg-gray-700 border-gray-600 text-white focus:ring-purple-500' : 'border-gray-300 focus:ring-purple-500';
  const filterBg = darkMode ? 'bg-gray-700/50' : 'bg-gray-50';
  const inwardBg = darkMode ? 'bg-green-900/30' : 'bg-green-50';
  const inwardText = darkMode ? 'text-green-300' : 'text-green-700';
  const outwardBg = darkMode ? 'bg-red-900/30' : 'bg-red-50';
  const outwardText = darkMode ? 'text-red-300' : 'text-red-700';
  const transferBg = darkMode ? 'bg-yellow-900/20' : 'bg-yellow-50';
  const transferHover = darkMode ? 'hover:bg-yellow-900/30' : 'hover:bg-yellow-100';
  const transferText = darkMode ? 'text-yellow-300' : 'text-yellow-700';
  const labelCls = darkMode ? 'text-gray-300' : 'text-gray-700';
  const selectCls = darkMode ? 'bg-gray-700 border-gray-600 text-white focus:ring-purple-500' : 'border-gray-300 focus:ring-blue-500';
  const paginationBtn = darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300 disabled:bg-gray-800 disabled:text-gray-600' : 'bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400';
  const sortBtn = darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50';

  return (
    <div ref={containerRef} className={`min-h-screen p-4 transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'} ${isFullscreen ? 'overflow-auto' : ''}`}>
      <div className="w-full mx-auto">
        {/* Header & Filters */}
        <div className={`rounded-xl shadow-sm border mb-4 transition-colors duration-300 ${cardCls}`}>
          <div className={`px-4 py-3 border-b ${borderCls}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white p-2 rounded-lg">
                  <FaFileExport className="w-4 h-4" />
                </div>
                <div>
                  <h1 className={`text-xl font-bold ${textP}`}>Tiles Day Book</h1>
                  <p className={`text-xs ${textS}`}>Daily tiles transactions report</p>
                </div>
              </div>
              <button onClick={toggleFullscreen} className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-gray-700 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'}`} title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
                {isFullscreen ? <FaCompress /> : <FaExpand />}
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className={`p-4 ${filterBg} transition-colors duration-300`}>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${labelCls}`}><FaCalendarAlt className="inline mr-2" />Date From *</label>
                <input type="date" value={filters.dateFrom} onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${inputCls}`} />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${labelCls}`}><FaCalendarAlt className="inline mr-2" />Date To *</label>
                <input type="date" value={filters.dateTo} onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${inputCls}`} />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${labelCls}`}><FaMapMarkerAlt className="inline mr-2" />Location</label>
                <Select value={filters.location} onChange={(v) => handleFilterChange('location', v)} options={locationOptions}
                  placeholder="All Locations" isClearable isSearchable styles={getSelectStyles(darkMode)} />
              </div>
              <div className="flex items-end gap-2">
                <button onClick={() => fetchDayBookData(1)} disabled={loading}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 py-2 rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                  {loading ? (<><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /><span>Loading...</span></>) : (<><FaSearch /><span>Generate</span></>)}
                </button>
              </div>
            </div>

            {/* Export and Pagination Controls */}
            {dayBookData.length > 0 && (
              <div className={`mt-4 pt-4 border-t ${borderCls}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm ${textS}`}>Export:</span>
                    <button onClick={exportToCSV} disabled={loading}
                      className="bg-emerald-600 text-white px-3 py-1 rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-40 flex items-center gap-2 text-sm">
                      <FaDownload />CSV
                    </button>
                    <button onClick={exportToExcel} disabled={loading}
                      className="bg-blue-600 text-white px-3 py-1 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-40 flex items-center gap-2 text-sm">
                      <FaFileExport />Excel
                    </button>
                  </div>
                  {pagination.totalPages > 1 && (
                    <div className="flex items-center gap-2">
                      <span className={`text-sm ${textS}`}>Page {pagination.currentPage} of {pagination.totalPages} ({pagination.totalEntries} total)</span>
                      <div className="flex gap-1">
                        <button onClick={() => handlePageChange(pagination.currentPage - 1)} disabled={!pagination.hasPrevPage || loading}
                          className={`p-1 rounded transition-colors ${paginationBtn}`}><FaArrowLeft className="w-4 h-4" /></button>
                        <button onClick={() => handlePageChange(pagination.currentPage + 1)} disabled={!pagination.hasNextPage || loading}
                          className={`p-1 rounded transition-colors ${paginationBtn}`}><FaArrowRight className="w-4 h-4" /></button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Page Size & Sort Controls */}
        {dayBookData.length > 0 && (
          <div className={`rounded-xl shadow-sm border mb-4 transition-colors duration-300 ${cardCls}`}>
            <div className="px-4 py-3 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <label className={`text-sm font-medium ${labelCls}`}>Per page:</label>
                <select value={filters.limit} onChange={(e) => { handleFilterChange('limit', parseInt(e.target.value)); fetchDayBookData(1); }}
                  className={`px-3 py-1 border rounded-lg focus:ring-2 focus:border-transparent text-sm transition-colors ${selectCls}`}>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                </select>
              </div>
              <div className="flex items-center gap-3">
                <label className={`text-sm font-medium ${labelCls}`}>Sort by:</label>
                <select value={filters.sortBy} onChange={(e) => { handleFilterChange('sortBy', e.target.value); fetchDayBookData(1); }}
                  className={`px-3 py-1 border rounded-lg focus:ring-2 focus:border-transparent text-sm transition-colors ${selectCls}`}>
                  <option value="date">Date</option>
                  <option value="id">ID</option>
                </select>
                <button onClick={() => { const newOrder = filters.sortOrder === 'asc' ? 'desc' : 'asc'; handleFilterChange('sortOrder', newOrder); fetchDayBookData(1); }}
                  className={`px-3 py-1 border rounded-lg text-sm flex items-center gap-1 transition-colors ${sortBtn}`}>
                  {filters.sortOrder === 'asc' ? '↑ Ascending' : '↓ Descending'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Results */}
        {Object.keys(groupedData).length > 0 ? (
          <div className={`rounded-xl shadow-sm border transition-colors duration-300 ${cardCls}`}>
            <div className={`px-4 py-3 border-b ${borderCls}`}>
              <div className="flex items-center justify-between">
                <h2 className={`text-lg font-semibold ${textP}`}>Day Book Report ({formatDate(filters.dateFrom)} to {formatDate(filters.dateTo)})</h2>
                <span className={`text-sm ${textS}`}>Total Entries: {dayBookData.length}</span>
              </div>
            </div>

            <div className="overflow-x-auto">
              {Object.entries(groupedData)
                .sort(([a], [b]) => new Date(a) - new Date(b))
                .map(([dateKey, entries]) => (
                <div key={dateKey} className={`border-b last:border-b-0 ${borderCls}`}>
                  {/* Date Header */}
                  <div className={`px-4 py-2 border-b ${borderCls} ${darkMode ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
                    <h3 className={`text-lg font-semibold ${darkMode ? 'text-blue-300' : 'text-blue-900'}`}>{formatDate(dateKey)}</h3>
                  </div>

                  {/* Entries Table */}
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead className={darkMode ? 'bg-gray-900/50' : 'bg-gray-50'}>
                        <tr>
                          <th className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Tile Details</th>
                          <th className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Location</th>
                          <th className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Supplier</th>
                          <th className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>ID</th>
                          <th className={`px-4 py-2 text-center text-xs font-medium uppercase tracking-wider ${inwardBg}`}>
                            <div className="flex items-center justify-center gap-1">
                              <FaArrowRight className={inwardText} />
                              <span className={inwardText}>Inward</span>
                            </div>
                          </th>
                          <th className={`px-4 py-2 text-center text-xs font-medium uppercase tracking-wider ${outwardBg}`}>
                            <div className="flex items-center justify-center gap-1">
                              <FaArrowLeft className={outwardText} />
                              <span className={outwardText}>Outward</span>
                            </div>
                          </th>
                        </tr>
                        <tr className={`border-t ${borderCls} ${darkMode ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
                          <th className="px-4 py-1" colSpan="4"></th>
                          <th className={`px-4 py-1 ${inwardBg}`}>
                            <div className={`grid grid-cols-4 gap-1 text-xs ${inwardText}`}>
                              <div>FS Box</div><div>FS Pcs</div><div>DS Box</div><div>DS Pcs</div>
                            </div>
                          </th>
                          <th className={`px-4 py-1 ${outwardBg}`}>
                            <div className={`grid grid-cols-4 gap-1 text-xs ${outwardText}`}>
                              <div>FS Box</div><div>FS Pcs</div><div>DS Box</div><div>DS Pcs</div>
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${darkMode ? 'divide-gray-700/50' : 'divide-gray-200'}`}>
                        {entries.map((entry, index) => (
                          <tr key={`${dateKey}_${index}`} className={`transition-colors ${entry.outward?.type === 'DEMOLISHED' ? (darkMode ? 'bg-orange-900/20 hover:bg-orange-900/30' : 'bg-orange-50 hover:bg-orange-100') : (darkMode ? 'hover:bg-gray-700/30' : 'hover:bg-gray-50')}`}>
                            <td className="px-4 py-2">
                              <div className={`font-medium ${textP}`}>{entry.tileName}</div>
                              <div className={`text-sm ${textS}`}>{entry.tileCode}</div>
                              {entry.outward?.type === 'DEMOLISHED' && (
                                <span className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-semibold ${darkMode ? 'bg-orange-900/50 text-orange-400' : 'bg-orange-100 text-orange-700'}`}>
                                  <FaTrashAlt className="w-3 h-3" /> DEMOLISHED
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-2">
                              <div className="flex items-center gap-2">
                                <FaMapMarkerAlt className={textM} />
                                <span className={`text-sm ${textP}`}>{entry.location?.name || entry.location?.locationName || 'N/A'}</span>
                              </div>
                            </td>
                            <td className={`px-4 py-2 text-sm ${textP}`}>{entry.inward?.supplier || 'N/A'}</td>
                            <td className={`px-4 py-2 text-sm ${textP}`}>{entry.inward?.id || entry.outward?.id || 'N/A'}</td>
                            <td className={`px-4 py-2 ${inwardBg}`}>
                              <div className={`grid grid-cols-4 gap-1 text-sm text-center font-medium ${inwardText}`}>
                                <div>{entry.inward?.fsBox || 0}</div>
                                <div>{entry.inward?.fsPieces || 0}</div>
                                <div>{entry.inward?.dsBox || 0}</div>
                                <div>{entry.inward?.dsPieces || 0}</div>
                              </div>
                            </td>
                            <td className={`px-4 py-2 ${entry.outward?.type === 'DEMOLISHED' ? (darkMode ? 'bg-orange-900/30' : 'bg-orange-50') : outwardBg}`}>
                              <div className={`grid grid-cols-4 gap-1 text-sm text-center font-medium ${entry.outward?.type === 'DEMOLISHED' ? (darkMode ? 'text-orange-300' : 'text-orange-700') : outwardText}`}>
                                <div>{entry.outward?.fsBox || 0}</div>
                                <div>{entry.outward?.fsPieces || 0}</div>
                                <div>{entry.outward?.dsBox || 0}</div>
                                <div>{entry.outward?.dsPieces || 0}</div>
                              </div>
                            </td>
                          </tr>
                        ))}

                        {/* Transfers */}
                        {entries.some(entry => entry.transfers?.length > 0) && (
                          entries.map((entry, entryIndex) =>
                            entry.transfers?.map((transfer, transferIndex) => (
                              <tr key={`${dateKey}_${entryIndex}_transfer_${transferIndex}`} className={`${transferBg} ${transferHover} transition-colors`}>
                                <td className="px-4 py-2">
                                  <div className="flex items-center gap-2">
                                    <FaTruck className={darkMode ? 'text-yellow-400' : 'text-yellow-600'} />
                                    <div>
                                      <div className={`font-medium ${textP}`}>{entry.tileName}</div>
                                      <div className={`text-sm ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>Transfer {transfer.type}</div>
                                    </div>
                                  </div>
                                </td>
                                <td className={`px-4 py-2 text-sm ${textP}`}>{entry.location?.name || entry.location?.locationName || 'N/A'}</td>
                                <td className={`px-4 py-2 text-sm ${textP}`}>{transfer.type === 'IN' ? `From: ${transfer.fromLocation}` : `To: ${transfer.toLocation}`}</td>
                                <td className={`px-4 py-2 text-sm ${textP}`}>{transfer.remarks || 'N/A'}</td>
                                <td className={`px-4 py-2 ${transferBg}`}>
                                  {transfer.type === 'IN' && (
                                    <div className={`grid grid-cols-4 gap-1 text-sm text-center font-medium ${transferText}`}>
                                      <div>{transfer.fsBox || 0}</div><div>{transfer.fsPieces || 0}</div>
                                      <div>{transfer.dsBox || 0}</div><div>{transfer.dsPieces || 0}</div>
                                    </div>
                                  )}
                                </td>
                                <td className={`px-4 py-2 ${transferBg}`}>
                                  {transfer.type === 'OUT' && (
                                    <div className={`grid grid-cols-4 gap-1 text-sm text-center font-medium ${transferText}`}>
                                      <div>{transfer.fsBox || 0}</div><div>{transfer.fsPieces || 0}</div>
                                      <div>{transfer.dsBox || 0}</div><div>{transfer.dsPieces || 0}</div>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ))
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : !loading && (
          <div className={`rounded-xl shadow-sm border p-8 text-center transition-colors duration-300 ${cardCls}`}>
            <FaWarehouse className={`mx-auto h-10 w-10 mb-3 ${textM}`} />
            <h3 className={`text-lg font-medium mb-1 ${textP}`}>No Data Found</h3>
            <p className={`text-sm ${textS}`}>
              {filters.dateFrom && filters.dateTo
                ? 'No tiles transactions found for the selected criteria.'
                : 'Please select date range and click Generate to view the day book report.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
